import { useRef, useState } from 'react';
import { post } from '../../../../api/baseUrl';
import type { TenancyTO } from '../../../../lib';
import {
  getConvertedFilters,
  sanitizeSearchFilterTokens,
} from '../../../pages/searchPage/utils';
import { useGlobalLoading, useTriggerUpdate } from '../../../../context';
import { SystemPermission } from '../../../../context';

export const useEndlessScrollPatientList = (initialMaxItems: number = 10) => {
  const { haveMoreData, resetHaveMoreData } = useTriggerUpdate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState([]);
  const [latestResponse, setLatestResponse] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [timeoutError, setTimeoutError] = useState(false);
  const { stopLoading } = useGlobalLoading();
  const abortControllerRef = useRef<AbortController | null>(null);
  const previousFilterRef = useRef<any>(null);

  const isTimeoutLikeError = (error: any) => {
    const message = String(error?.message || '').toLowerCase();
    const code = String(error?.code || '');
    return (
      message.includes('timeout') ||
      message.includes('network error') ||
      message.includes('bad gateway') ||
      message.includes('gateway timeout') ||
      message.includes('failed to fetch') ||
      code === 'ECONNABORTED' ||
      code === 'ERR_NETWORK' ||
      code === 'ETIMEDOUT'
    );
  };

  const hasFilterChanged = (
    previousFilter: any,
    currentFilter: any,
  ): boolean => {
    if (!previousFilter || !currentFilter) return true;

    return Object.keys(currentFilter).some(
      (key) =>
        JSON.stringify(previousFilter[key]) !==
        JSON.stringify(currentFilter[key]),
    );
  };

  const getPatientList = async (
    page: number,
    sort: { sort: string; direction: string },
    filter: any,
    selectedTenancy: TenancyTO,
    searchFilters?: string[],
  ) => {
    if (!selectedTenancy || !selectedTenancy.tenancy_id) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsLoading(true);

    const newController = new AbortController();
    abortControllerRef.current = newController;
    const shouldFetch =
      haveMoreData ||
      page === 1 ||
      hasFilterChanged(previousFilterRef.current, filter) ||
      (searchFilters && searchFilters.length > 0);
    if (shouldFetch) {
      setError(null);
      setTimeoutError(false);
      const searchFiltersMap = selectedTenancy?.features?.searchFiltersMap;
      const canAccessOpenSafetyAlerts =
        !selectedTenancy?.permissionsSetup ||
        !!selectedTenancy?.userPermissions?.[
          SystemPermission.SAFETY_MONITORING
        ];
      const canAccessReadyForReview =
        !selectedTenancy?.permissionsSetup ||
        !selectedTenancy?.userPermissions?.[SystemPermission.DEIDENTIFIED_DATA];
      const sanitizedSearchFilters = searchFilters
        ? sanitizeSearchFilterTokens(searchFilters, searchFiltersMap, {
            allowOpenSafetyAlerts: canAccessOpenSafetyAlerts,
            allowReadyForReview: canAccessReadyForReview,
          })
        : undefined;
      const convertedFilters = sanitizedSearchFilters
        ? getConvertedFilters(sanitizedSearchFilters, searchFiltersMap)
        : undefined;
      const convertedFiltersArg =
        convertedFilters && convertedFilters.length > 0
          ? convertedFilters
          : undefined;
      try {
        const body = {
          tenancyId: selectedTenancy.tenancy_id, //tenancyId which you are searching in
          clinics: filter.clinics, //.split(',').map(Number), // array of clinic id ints
          primaryTeams: filter.primaryTeams, // array of search strings
          healthProfessionals: filter.healthProfessionals, // array of search strings
          episodeStatus: filter.episodeStatus, //open or closed
          insightCategory: filter.insightCategory, // array of search chars (h, m, l?)
          tags: filter.tags, // arrray of search strings
          patientName: filter.patientName, //free test search against firstname and last name
          event_subcategory_id: filter.event_subcategory_id,
          event_subcategory_name: filter.event_subcategory_name,
          assigned: filter.assigned, //
          favourite: filter.favourite, //
          withingsConnected:
            filter.withingsConnected === true ||
            filter.withingsConnected === null
              ? undefined
              : filter.withingsConnected, //
          page, //page num
          max: initialMaxItems, //amount per page
          sort: sort.sort, // first_name or last_name
          direction: sort.direction, //ASC or DESC
          searchFilters: convertedFiltersArg,
        };

        const results = await post(
          `getPatientList`,
          body,
          undefined,
          undefined,
          abortControllerRef.current.signal,
        );

        if (!results || !results?.data) {
          return;
        }

        const { data } = results;

        if (data.message == 'Error') {
          throw new Error('Error [getPatientList]');
        }
        resetHaveMoreData(!!data.config?.haveMoreData);
        setLatestResponse(data.data);

        if (hasFilterChanged(previousFilterRef.current, filter)) {
          setResponse(data.data || []);
          previousFilterRef.current = filter;
        } else {
          setResponse((currentData) => {
            if (page > 1) {
              return [...currentData, ...data.data];
            } else {
              return data.data || [];
            }
          });
        }
      } catch (error: any) {
        const isTimeout = isTimeoutLikeError(error);
        setTimeoutError(isTimeout);
        setError(isTimeout ? 'Timeout' : null);
        console.error('An error occurred during getPatientList:', error);
        setResponse([]);
      } finally {
        if (abortControllerRef.current?.signal === newController.signal) {
          setIsLoading(false);
          stopLoading();
          abortControllerRef.current = null;
        }
      }
    } else {
      if (abortControllerRef.current?.signal === newController.signal) {
        setIsLoading(false);
        stopLoading();
        setResponse([]);
        abortControllerRef.current = null;
      }
    }
  };

  const resetResponse = () => {
    setResponse([]);
    resetHaveMoreData(true);
  };

  return {
    setCurrentPage,
    currentPage,
    isLoading,
    response,
    error,
    timeoutError,
    getPatientList,
    resetResponse,
    setIsLoading,
    latestResponse,
  };
};
